import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Search, Sparkles, Zap, Globe, ArrowRight, Plus, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_CONFIG } from '../../config/api';

type ResearchLength = 'quick_scan' | 'targeted' | 'deep_dive';

interface HomePageProps {
  onStartResearch?: (query: string, apiKey?: string, researchLength?: ResearchLength) => void;
  onCreateTemplate?: (query: string, apiKey?: string, researchLength?: ResearchLength) => void;
  currentApiKey?: string;
  onApiKeyUpdate?: (key: string) => void;
  isDarkMode?: boolean;
  onToggleTheme?: () => void;
}

export const HomePage: React.FC<HomePageProps> = ({ 
  onStartResearch, 
  onCreateTemplate,
  currentApiKey,
  onApiKeyUpdate,
  isDarkMode = false,
  onToggleTheme
}) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { 
    apiKey, 
    setApiKey, 
    isKeyRequired, 
    setIsKeyRequired 
  } = useAppStore();

  const [query, setQuery] = useState('');
  const [researchLength, setResearchLength] = useState<ResearchLength>('targeted');
  const [showResearchInfo, setShowResearchInfo] = useState(false);
  const [showSliderLabel, setShowSliderLabel] = useState(false);
  const [isCheckingRequirement, setIsCheckingRequirement] = useState(true);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  // Check if API key is required on component mount
  useEffect(() => {
    const checkKeyRequirement = async () => {
      if (!user) {
        setIsCheckingRequirement(false);
        setIsKeyRequired(true);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(API_CONFIG.endpoints.config.keyRequired, {
          headers: API_CONFIG.getAuthHeaders(token)
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsKeyRequired(data.key_required);
        } else {
          setIsKeyRequired(true);
        }
      } catch (error) {
        console.error('Failed to check key requirement:', error);
        setIsKeyRequired(true);
      } finally {
        setIsCheckingRequirement(false);
      }
    };

    checkKeyRequirement();
  }, [user, getAccessTokenSilently, setIsKeyRequired]);

  // Handle clicks outside tooltip to close it (but not on the info button itself)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
          infoButtonRef.current && !infoButtonRef.current.contains(event.target as Node)) {
        setShowResearchInfo(false);
      }
    };

    if (showResearchInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showResearchInfo]);

  const handleStartResearch = () => {
    if (query.trim() && (!isKeyRequired || apiKey)) {
      if (onStartResearch) {
        onStartResearch(query.trim(), apiKey || undefined, researchLength);
      } else {
        // Default behavior - navigate to research page
        navigate('/research', { 
          state: { 
            query: query.trim(), 
            apiKey: apiKey || undefined, 
            researchLength 
          } 
        });
      }
    }
  };

  const handleCreateTemplate = () => {
    if (query.trim() && (!isKeyRequired || apiKey)) {
      if (onCreateTemplate) {
        onCreateTemplate(query.trim(), apiKey || undefined, researchLength);
      } else {
        // Default behavior - navigate to AI template creation
        navigate('/templates/create', { 
          state: { 
            query: query.trim(), 
            apiKey: apiKey || undefined, 
            researchLength 
          } 
        });
      }
    }
  };


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleStartResearch();
    }
  };

  const getResearchLengthInfo = (length: ResearchLength) => {
    switch (length) {
      case 'quick_scan':
        return {
          label: 'Quick Scan',
          description: 'Fast, focused analysis for quick insights',
          color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
          details: 'Best when you already know what to check—fast answers with tight citations'
        };
      case 'targeted':
        return {
          label: 'Targeted',
          description: 'Balanced coverage for comprehensive research',
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
          details: 'Ideal for due diligence, strategy docs, and market scans—finds gaps, risks, and opportunities'
        };
      case 'deep_dive':
        return {
          label: 'Deep Dive',
          description: 'Extensive analysis with maximum detail',
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
          details: 'Maximum coverage—wide net, deep analysis, and a defensible narrative for high-stakes decisions'
        };
    }
  };

  const canSubmit = query.trim().length > 0 && (!isKeyRequired || apiKey);
  const currentLengthInfo = getResearchLengthInfo(researchLength);

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-950 dark:to-indigo-950">
      <div className="container mx-auto px-4 py-7 max-w-4xl">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-3"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3 flex items-center justify-center gap-2">
            Welcome back, {user?.name?.split(' ')[0] || 'Researcher'}
            <motion.div
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.2, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Sparkles className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </motion.div>
          </h1>
          
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-1">
            Start your analysis or create a reusable template
          </p>
          
        </motion.div>

        {/* Main Research Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="shadow-xl border-0 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Search className="w-6 h-6 text-sky-600" />
                Research Query
              </CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Query Input */}
              <div className="space-y-2">
                <div className="relative">
                  <Textarea
                    placeholder="What would you like to research today? (e.g., 'Latest trends in AI healthcare applications', 'Market analysis for electric vehicles in 2025')"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="min-h-[100px] text-base resize-none border-gray-200 dark:border-gray-700 focus:ring-sky-500 focus:border-sky-500"
                  />
                  <div className="absolute bottom-3 left-3 text-xs text-gray-500 dark:text-gray-400">
                    Press Enter to start research
                  </div>
                  <div className="absolute bottom-3 right-3 text-xs text-gray-500 dark:text-gray-400">
                    {query.length} characters
                  </div>
                </div>
              </div>

              {/* Research Depth - Clean Slider */}
              <div className="space-y-1">
                <div className="flex items-center space-x-4 py-0">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Research Depth
                  </label>
                  
                  <Button
                    ref={infoButtonRef}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowResearchInfo(!showResearchInfo)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <Info className="w-4 h-4" />
                  </Button>
                  
                  {/* Clean Slider */}
                  <div className="flex-1 max-w-48 relative">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="1"
                      value={researchLength === 'quick_scan' ? 0 : researchLength === 'targeted' ? 1 : 2}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        const newLength = value === 0 ? 'quick_scan' : value === 1 ? 'targeted' : 'deep_dive';
                        setResearchLength(newLength);
                        setShowSliderLabel(true);
                      }}
                      onMouseDown={() => setShowSliderLabel(true)}
                      onMouseUp={() => setTimeout(() => setShowSliderLabel(false), 2000)}
                      onTouchStart={() => setShowSliderLabel(true)}
                      onTouchEnd={() => setTimeout(() => setShowSliderLabel(false), 2000)}
                      className="w-full h-1 bg-gray-300 dark:bg-gray-600 appearance-none cursor-pointer slider-thin"
                      style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                      }}
                    />
                    
                    {/* Slider Label Popup - Positioned above the slider thumb */}
                    <AnimatePresence>
                      {showSliderLabel && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.9 }}
                          animate={{ opacity: 1, y: -8, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.9 }}
                          className="absolute -top-8 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap pointer-events-none z-10 shadow-lg"
                          style={{
                            left: `${(researchLength === 'quick_scan' ? 6 : researchLength === 'targeted' ? 50 : 94)}%`,
                            transform: 'translateX(-50%)'
                          }}
                        >
                          {getResearchLengthInfo(researchLength).label}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-[1px] w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <AnimatePresence>
                  {showResearchInfo && (
                    <motion.div
                      ref={tooltipRef}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                    >
                      <div className="space-y-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          Try this
                        </h4>
                        {(['quick_scan', 'targeted', 'deep_dive'] as ResearchLength[]).map((length) => {
                          const info = getResearchLengthInfo(length);
                          return (
                            <div key={length} className="flex items-start gap-3">
                              <Badge className={info.color} variant="outline">
                                {info.label}
                              </Badge>
                              <div className="flex-1 space-y-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {info.description}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  {info.details}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Custom slider styles */}
                <style dangerouslySetInnerHTML={{
                  __html: `
                    input[type="range"].slider-thin::-webkit-slider-thumb {
                      appearance: none;
                      width: 12px;
                      height: 12px;
                      background: #7c3aed;
                      border-radius: 50%;
                      cursor: pointer;
                      border: 1px solid #f5f5f4;
                      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                    }
                    
                    input[type="range"].slider-thin::-webkit-slider-thumb:hover {
                      background: #6d28d9;
                      transform: scale(1.1);
                    }
                    
                    input[type="range"].slider-thin::-moz-range-thumb {
                      width: 12px;
                      height: 12px;
                      background: #7c3aed;
                      border-radius: 50%;
                      cursor: pointer;
                      border: 1px solid #f5f5f4;
                      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                      -moz-appearance: none;
                      appearance: none;
                    }
                    
                    input[type="range"].slider-thin::-moz-range-thumb:hover {
                      background: #6d28d9;
                    }
                    
                    /* Dark mode thumb */
                    .dark input[type="range"].slider-thin::-webkit-slider-thumb {
                      background: #8b5cf6;
                      border: 1px solid #374151;
                    }
                    
                    .dark input[type="range"].slider-thin::-webkit-slider-thumb:hover {
                      background: #a78bfa;
                    }
                    
                    .dark input[type="range"].slider-thin::-moz-range-thumb {
                      background: #8b5cf6;
                      border: 1px solid #374151;
                    }
                    
                    .dark input[type="range"].slider-thin::-moz-range-thumb:hover {
                      background: #a78bfa;
                    }
                    
                    /* Remove focus outline/highlight */
                    input[type="range"].slider-thin:focus {
                      outline: none;
                      box-shadow: none;
                    }
                    
                    input[type="range"].slider-thin:focus::-webkit-slider-thumb {
                      outline: none;
                      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                    }
                    
                    input[type="range"].slider-thin:focus::-moz-range-thumb {
                      outline: none;
                      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                    }
                  `
                }} />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleCreateTemplate}
                  disabled={!canSubmit}
                  size="lg"
                  variant="outline"
                  className="flex-1 border-sky-300 dark:border-sky-600 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/30 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Template
                  <Plus className="w-5 h-5 ml-2" />
                </Button>
                
                <Button 
                  onClick={handleStartResearch}
                  disabled={!canSubmit}
                  size="lg"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Start Research
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              {/* Suggested Questions */}
              <div className="pt-0">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Try these popular questions:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    "What are the latest trends in artificial intelligence for healthcare?",
                    "How is the electric vehicle market evolving in 2025?",
                    "What are the most promising renewable energy technologies?",
                    "How is remote work changing corporate culture and productivity?"
                  ].map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setQuery(suggestion)}
                      className="text-left p-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600"
                    >
                      💡 {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              

              {/* API Key Warning */}
              {isKeyRequired && !apiKey && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    ⚠️ API key required to proceed. Click the "Add API Key" button in the header to add your OpenAI API key.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        
      </div>
    </div>
  );
};